var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * LaunchHandler Component
 *
 * Handles SMART on FHIR launch flow using secure backend-only OAuth.
 *
 * SECURITY:
 * - NO client credentials in frontend
 * - NO access tokens in frontend
 * - NO PKCE parameters in frontend
 * - Uses session tokens (UUIDs) only
 *
 * Flow:
 * 1. EHR launches app with iss + launch parameters
 * 2. Send to backend, get authorization URL
 * 3. Redirect user to EHR authorization
 * 4. EHR redirects to backend callback
 * 5. Backend redirects here with session_token
 * 6. Store session token, fetch session info
 * 7. Render app with authenticated context
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { setLoading, setAuthenticated, setSessionInfo, setError, clearError } from '../../store/smart/smartSlice';
import { initiateLaunch, getSessionInfo, logout, isAuthSuccessInUrl, extractErrorFromUrl, cleanUrl, logAuthStep, parseError } from '../../services/smartAuthService';
import { getInstanceId } from '../../services/scopedStorage';
import { trackPendoEvent } from '../../services/pendoService';
import { PATIENT_LAUNCHED } from '../../constants/pendoEvents';
const LaunchHandler = ({ children }) => {
    const { t } = useTranslation('translation', {
        keyPrefix: 'LAUNCH_HANDLER'
    });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Use ref instead of state to prevent race condition in React.StrictMode
    // When StrictMode runs useEffect twice, both executions see processing=false
    // before the first setProcessing(true) takes effect
    const processingRef = React.useRef(false);
    const { isAuthenticated, isLoading, error } = useSelector((state) => state.smart);
    useEffect(() => {
        // Prevent multiple simultaneous auth attempts
        if (processingRef.current || isLoading) {
            return;
        }
        const handleAuthentication = () => __awaiter(void 0, void 0, void 0, function* () {
            processingRef.current = true;
            try {
                // ========================================
                // STEP 1: Check for OAuth callback
                // ========================================
                // Check for error in callback
                const urlError = extractErrorFromUrl();
                if (urlError) {
                    logAuthStep('Callback Error', { error: urlError.error });
                    dispatch(setError(urlError.description || `Authorization failed: ${urlError.error}`));
                    cleanUrl();
                    processingRef.current = false;
                    return;
                }
                // Check for session token in callback
                // SECURITY: Check for auth success flag (session token is in secure cookie)
                if (isAuthSuccessInUrl()) {
                    logAuthStep('Callback Success', { authSuccess: true });
                    // NO need to store session token - it's in secure HTTP-only cookie!
                    // NO need to initialize FHIR client with token - cookies sent automatically!
                    // Clean URL (remove query parameters)
                    // EPIC HYPERSPACE SUPPORT: cleanUrl preserves the instance ID
                    cleanUrl();
                    // Fetch session info from backend (cookie sent automatically)
                    dispatch(setLoading(true));
                    const sessionInfo = yield getSessionInfo();
                    // BACKEND UPDATE HANDLING:
                    // The backend no longer sends sessionToken or launchContextId in the response body for security.
                    // We MUST ensure the sessionToken is populated in the Redux state so the UI knows we are authenticated.
                    // We use the public instance ID (from URL) as the "session token" identifier for the frontend state.
                    if (!sessionInfo.sessionToken) {
                        const instanceId = getInstanceId();
                        if (instanceId) {
                            sessionInfo.sessionToken = instanceId;
                        }
                    }
                    logAuthStep('Session Info Retrieved', {
                        patient: sessionInfo.patient,
                        fhirUser: sessionInfo.fhirUser,
                        hasRefreshToken: sessionInfo.hasRefreshToken
                    });
                    // Update Redux state
                    dispatch(setSessionInfo(sessionInfo));
                    dispatch(setAuthenticated(true));
                    dispatch(setLoading(false));
                    trackPendoEvent(PATIENT_LAUNCHED);
                    processingRef.current = false;
                    return;
                }
                // ========================================
                // STEP 2: Check for EHR launch parameters
                // ========================================
                const launch = searchParams.get('launch');
                const iss = searchParams.get('iss');
                if (iss) {
                    logAuthStep('EHR Launch Detected', {
                        iss: iss,
                        hasLaunchToken: !!launch
                    });
                    dispatch(setLoading(true));
                    // Send launch parameters to backend
                    const launchResponse = yield initiateLaunch({
                        iss,
                        launch: launch || undefined
                    });
                    // Check for errors
                    if (launchResponse.error) {
                        throw new Error(launchResponse.errorDescription || launchResponse.error);
                    }
                    // Check for authorization URL
                    if (!launchResponse.authorizationUrl) {
                        throw new Error('Backend did not return authorization URL');
                    }
                    logAuthStep('Authorization URL Received');
                    // Redirect to EHR authorization page
                    // Note: This leaves the current page, so no cleanup needed
                    window.location.href = launchResponse.authorizationUrl;
                    // Don't set processing to false - we're redirecting
                    return;
                }
                // ========================================
                // STEP 3: Check for existing session (in secure cookie)
                // ========================================
                if (!isAuthenticated) {
                    // SECURITY: Check if session exists by calling backend (async now)
                    logAuthStep('Checking for existing session');
                    try {
                        dispatch(setLoading(true));
                        // Session token is in secure cookie - backend will validate
                        const sessionInfo = yield getSessionInfo();
                        // BACKEND UPDATE HANDLING:
                        // Ensure sessionToken is populated for Redux state consistency
                        if (!sessionInfo.sessionToken) {
                            const instanceId = getInstanceId();
                            if (instanceId) {
                                sessionInfo.sessionToken = instanceId;
                            }
                        }
                        logAuthStep('Existing Session Found');
                        dispatch(setSessionInfo(sessionInfo));
                        dispatch(setAuthenticated(true));
                        dispatch(setLoading(false));
                        trackPendoEvent(PATIENT_LAUNCHED);
                    }
                    catch (err) {
                        logAuthStep('No valid session', { error: parseError(err) });
                        // No valid session - user needs to authenticate
                        dispatch(setLoading(false));
                    }
                }
                processingRef.current = false;
            }
            catch (err) {
                dispatch(setError(parseError(err)));
                dispatch(setLoading(false));
                processingRef.current = false;
            }
        });
        handleAuthentication();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    // ==========================================
    // LOADING STATE
    // ==========================================
    if (isLoading) {
        return (_jsxs(Box, { sx: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                gap: 2,
                p: 3
            }, children: [_jsx(CircularProgress, { size: 60 }), _jsx(Typography, { variant: "h6", color: "text.secondary", children: t('AUTHENTICATING') }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: t('WAIT_MESSAGE') })] }));
    }
    // ==========================================
    // ERROR STATE
    // ==========================================
    if (error) {
        return (_jsx(Box, { sx: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                gap: 2,
                p: 3
            }, children: _jsxs(Alert, { severity: "error", sx: { maxWidth: 600 }, action: _jsx(Button, { color: "inherit", size: "small", onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                        dispatch(clearError());
                        // Clear session on backend and frontend (scoped storage)
                        yield logout();
                        navigate('/');
                    }), children: t('RETRY') }), children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: t('AUTH_ERROR_TITLE') }), _jsx(Typography, { variant: "body2", children: error })] }) }));
    }
    // ==========================================
    // NOT AUTHENTICATED STATE
    // ==========================================
    if (!isAuthenticated) {
        return (_jsx(Box, { sx: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                gap: 2,
                p: 3
            }, children: _jsxs(Alert, { severity: "info", sx: { maxWidth: 600 }, children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: t('LAUNCH_REQUIRED_TITLE') }), _jsx(Typography, { variant: "body2", paragraph: true, children: t('LAUNCH_REQUIRED_DESCRIPTION') }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: t('LAUNCH_INSTRUCTIONS') })] }) }));
    }
    // ==========================================
    // AUTHENTICATED - RENDER CHILDREN
    // ==========================================
    return _jsx(_Fragment, { children: children });
};
export default LaunchHandler;
